import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type HeaderCheckboxState = 'none' | 'some' | 'all';

interface UseRowSelectionOptions {
  /** Flat ordered list of item IDs that are currently visible / selectable. */
  itemIds: string[];
}

interface UseRowSelectionReturn {
  selectedIds: Set<string>;
  isSelected: (id: string) => boolean;
  /** Toggle a single item. Pass `shiftKey=true` for range-select. */
  toggle: (id: string, shiftKey?: boolean) => void;
  selectAll: () => void;
  clearAll: () => void;
  headerCheckboxState: HeaderCheckboxState;
}

/**
 * Reusable multi-select hook with Shift+click range support.
 *
 * - Tracks a `Set<string>` of selected IDs.
 * - `toggle(id, shiftKey)` does single toggle or range-select.
 * - Auto-prunes stale IDs when `itemIds` changes (e.g. positions removed by filter / refresh).
 * - Provides `headerCheckboxState` for the indeterminate "select all" checkbox.
 */
export function useRowSelection({ itemIds }: UseRowSelectionOptions): UseRowSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedRef = useRef<string | null>(null);

  // Prune stale IDs whenever itemIds changes.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const validSet = new Set(itemIds);
      const pruned = new Set<string>();
      Array.from(prev).forEach((id) => {
        if (validSet.has(id)) pruned.add(id);
      });
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [itemIds]);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const toggle = useCallback(
    (id: string, shiftKey = false) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);

        if (shiftKey && lastClickedRef.current && lastClickedRef.current !== id) {
          // Range select between lastClicked and id.
          const fromIdx = itemIds.indexOf(lastClickedRef.current);
          const toIdx = itemIds.indexOf(id);
          if (fromIdx !== -1 && toIdx !== -1) {
            const start = Math.min(fromIdx, toIdx);
            const end = Math.max(fromIdx, toIdx);
            for (let i = start; i <= end; i++) {
              next.add(itemIds[i]);
            }
          } else {
            // Fallback to simple toggle.
            if (next.has(id)) next.delete(id);
            else next.add(id);
          }
        } else {
          if (next.has(id)) next.delete(id);
          else next.add(id);
        }

        lastClickedRef.current = id;
        return next;
      });
    },
    [itemIds],
  );

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(itemIds));
    lastClickedRef.current = null;
  }, [itemIds]);

  const clearAll = useCallback(() => {
    setSelectedIds(new Set());
    lastClickedRef.current = null;
  }, []);

  const headerCheckboxState: HeaderCheckboxState = useMemo(() => {
    if (selectedIds.size === 0 || itemIds.length === 0) return 'none';
    if (selectedIds.size >= itemIds.length) return 'all';
    return 'some';
  }, [selectedIds.size, itemIds.length]);

  return { selectedIds, isSelected, toggle, selectAll, clearAll, headerCheckboxState };
}
